import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { useLiveQuery } from 'dexie-react-hooks';
import { useNavigate } from 'react-router';

import { paths } from '@/app/router/paths';
import { Button, Card, MonoLabel, Skeleton } from '@/design-system';
import { inspectionMutations } from '@/entities/inspection';
import { formatBytes } from '@/entities/order';
import { useSessionStore } from '@/shared/api';
import { useTenantDb } from '@/shared/offline';
import { CardTitle, Screen, ScreenBody, ScreenHeader } from '@/shared/ui';

import { SyncPill } from './components/SyncPill';
import { myDayCopy } from './copy';
import { buildLocalDay, type LocalStop } from './MyDayModel';
import { MyDayStop } from './MyDayStop';

/**
 * Scherm 3 - Mijn dag.
 *
 * De opdrachten komen uit de lokale opslag van de actieve omgeving - wat
 * `/sync/bootstrap` voor deze inspecteur klaarzette - dus het scherm en
 * "Starten" werken ook in een kruipruimte zonder netwerk. Vanaf elke stop is
 * het één tik naar de juiste opname: een energielabel gaat naar het
 * NTA 8800-scherm, de rest naar de NTA 8060-opname.
 */
export function MyDayPage() {
    const navigate = useNavigate();
    const db = useTenantDb();
    /* Tijdens het meekijken is er bewust geen lokale database: zie `tenantScopeOf`. */
    const impersonating = useSessionStore((state) => state.impersonation !== null);

    const day = useLiveQuery(async () => {
        if (!db) {
            return null;
        }

        const [orders, inspections, versions, photos, bootstrapped, changes] = await Promise.all([
            db.orders.toArray(),
            db.inspections.toArray(),
            db.templateVersions.toArray(),
            db.photos.toArray(),
            db.syncMeta.get('bootstrapped-at'),
            db.outbox.count(),
        ]);

        return {
            ...buildLocalDay({
                orders,
                inspections,
                versions,
                photos,
                now: new Date(),
                userUuid: db.scope.userUuid,
            }),
            bootstrapped: bootstrapped !== undefined,
            changes,
            photosWaiting: photos.filter(
                (photo) =>
                    photo.deleted === 1 || photo.state === 'pending' || photo.state === 'uploading',
            ).length,
        };
    }, [db]);

    const openSurvey = async (stop: LocalStop) => {
        if (!stop.inspectionUuid) {
            return;
        }

        if (!stop.submitted) {
            try {
                await inspectionMutations.start(stop.inspectionUuid, db ?? undefined);
            } catch (cause) {
                /* Starten mag de opname nooit tegenhouden; het volgende antwoord start hem alsnog. */
                console.error('[mijn dag] opname starten mislukt', cause);
            }
        }

        void navigate(
            stop.energyLabel
                ? paths.energyPerformance(stop.inspectionUuid)
                : paths.survey(stop.inspectionUuid),
        );
    };

    const stops = day?.today ?? [];
    const open = (stop: LocalStop) => stop.inspectionUuid !== null && !stop.submitted;
    const next =
        stops.find((stop) => stop.resuming && !stop.submitted) ??
        stops.find(open) ??
        day?.upcoming.find(open);

    return (
        <Screen>
            <ScreenHeader
                eyebrow={myDayCopy.eyebrowLocal(
                    format(new Date(), 'EEEE d MMMM', { locale: nl }),
                    stops.length,
                )}
                title={myDayCopy.title}
                actions={
                    <>
                        <SyncPill />
                        <Button
                            variant="primary"
                            size="compact"
                            disabled={next === undefined}
                            onClick={() => next && void openSurvey(next)}
                        >
                            {myDayCopy.startNext}
                        </Button>
                    </>
                }
            />

            <ScreenBody>
                <div className="grid items-start gap-[14px] [grid-template-columns:repeat(auto-fit,minmax(320px,1fr))]">
                    <div className="flex flex-col gap-[10px]">
                        {!db && impersonating ? (
                            <Card padding="md">
                                <CardTitle className="mb-2">
                                    {myDayCopy.impersonatingTitle}
                                </CardTitle>
                                <p className="m-0 mb-3 text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                                    {myDayCopy.impersonatingBody}
                                </p>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => void navigate(paths.inspections)}
                                >
                                    {myDayCopy.toInspections}
                                </Button>
                            </Card>
                        ) : !db ? (
                            <Card padding="md">
                                <CardTitle className="mb-2">
                                    {myDayCopy.noEnvironmentTitle}
                                </CardTitle>
                                <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                                    {myDayCopy.noEnvironmentBody}
                                </p>
                            </Card>
                        ) : day === undefined || day === null ? (
                            <Skeleton height={110} radius={10} pulse label={myDayCopy.loading} />
                        ) : (
                            <>
                                {stops.length === 0 ? (
                                    <Card padding="md">
                                        <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                                            {day.bootstrapped
                                                ? myDayCopy.stopsEmpty
                                                : myDayCopy.notBootstrapped}
                                        </p>
                                    </Card>
                                ) : (
                                    stops.map((stop) => (
                                        <MyDayStop
                                            key={stop.orderUuid}
                                            stop={stop}
                                            onStart={(target) => void openSurvey(target)}
                                            onOpenOrder={(target) =>
                                                void navigate(paths.dossier(target.orderUuid))
                                            }
                                        />
                                    ))
                                )}

                                {day.upcoming.length > 0 ? (
                                    <>
                                        <h2 className="m-0 mt-2 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]">
                                            {myDayCopy.upcomingTitle}
                                        </h2>
                                        {day.upcoming.map((stop) => (
                                            <MyDayStop
                                                key={stop.orderUuid}
                                                stop={stop}
                                                onStart={(target) => void openSurvey(target)}
                                                onOpenOrder={(target) =>
                                                    void navigate(paths.dossier(target.orderUuid))
                                                }
                                            />
                                        ))}
                                    </>
                                ) : null}
                            </>
                        )}
                    </div>

                    {/*
                     * Geen routekaart: zolang er geen echte kaart en geen rijtijd is, zou
                     * dat een leeg vlak met een streepje zijn. Hij komt terug met een kaart.
                     * Zonder lokale database staat er ook niets offline klaar om over te
                     * vertellen.
                     */}
                    {db ? (
                        <div className="flex flex-col gap-[14px]">
                            <Card padding="md">
                                <CardTitle className="mb-3">{myDayCopy.offlineTitle}</CardTitle>
                                <p className="m-0 mb-3 text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                                    {myDayCopy.offlineBody({
                                        total: day?.offline.total ?? 0,
                                        ready: day?.offline.ready ?? 0,
                                        withoutSurvey: day?.offline.withoutSurvey ?? 0,
                                        changes: day?.changes ?? 0,
                                        photos: day?.photosWaiting ?? 0,
                                        synced: day?.bootstrapped ?? false,
                                    })}
                                </p>
                                <div className="flex items-center gap-2">
                                    <span
                                        aria-hidden="true"
                                        className="h-[6px] w-[6px] shrink-0 rounded-full bg-[var(--accent)]"
                                    />
                                    <MonoLabel size={11} tone="accent">
                                        {myDayCopy.offlineReady(
                                            day?.offline.ready ?? 0,
                                            day?.offline.total ?? 0,
                                            formatBytes(day?.offline.bytes ?? 0),
                                        )}
                                    </MonoLabel>
                                </div>
                            </Card>
                        </div>
                    ) : null}
                </div>
            </ScreenBody>
        </Screen>
    );
}
